import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { cellStateLabel, modeLabel } from "../helper/repeatabilityText";
import { TOOLTIP_MUTED as MUTED } from "../theme/tooltipTheme";
import { rgbString } from "../helper/colorConversion";

function LabValue({ label, value }) {
    return (
        <Stack sx={{ minWidth: 44 }}>
            <Typography sx={{ fontSize: 10, color: MUTED, lineHeight: 1.2 }}>{label}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, fontVariantNumeric: "tabular-nums" }}>
                {value.toFixed(1)}
            </Typography>
        </Stack>
    );
}

/** Verdict label: green for a pass, red for a rejection, grey when the
 *  question did not arise (e.g. no eye check on a patch that failed ΔE). */
function VerdictChip({ tone, children }) {
    const colors = {
        pass: { bg: "rgba(52,211,153,0.18)", fg: "#34d399" },
        reject: { bg: "rgba(248,113,113,0.2)", fg: "#f87171" },
        muted: { bg: "rgba(255,255,255,0.08)", fg: MUTED },
    }[tone];
    return (
        <Box
            component="span"
            sx={{
                px: 0.75, py: 0.1, borderRadius: 1, fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
                textTransform: "uppercase", whiteSpace: "nowrap",
                bgcolor: colors.bg, color: colors.fg,
            }}
        >
            {children}
        </Box>
    );
}

/** Hover card of one patch: swatch + position, one row per capture mode
 *  (L*, a*, b*, ΔE00 vs its limit, pass/fail), and the patch's state. */
export default function RepeatabilityCellCard({ cell, limits, paintMode, showDrift = false }) {
    const shown = cell.modes[String(paintMode)] || cell.modes["1"];
    return (
        <Stack spacing={1.25} sx={{ minWidth: 250 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                    sx={{
                        width: 30, height: 30, borderRadius: 1, flexShrink: 0,
                        bgcolor: rgbString(shown.rgb), border: "1px solid rgba(255,255,255,0.25)",
                    }}
                />
                <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25 }}>
                        {`Row ${cell.row + 1} · Column ${cell.col + 1}`}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: MUTED, lineHeight: 1.25 }}>
                        {`Batch ${cell.batch + 1}`}
                        {cell.width_mm ? ` · ${(cell.width_mm * 1000).toFixed(1)} µm` : ""}
                    </Typography>
                </Box>
            </Stack>
            {Object.entries(cell.modes).map(([mode, reading]) => {
                const limit = limits?.[Number(mode) - 1];
                return (
                    <Box
                        key={mode}
                        sx={{ pt: 1, borderTop: "1px solid rgba(255,255,255,0.12)" }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: MUTED }}>
                                {modeLabel(mode)}
                            </Typography>
                            <VerdictChip tone={reading.repeatable ? "pass" : "reject"}>
                                {reading.repeatable ? "Pass ΔE" : "Rejected ΔE"}
                            </VerdictChip>
                        </Stack>
                        <Stack direction="row" spacing={1.5} alignItems="flex-end">
                            <LabValue label="L*" value={reading.lab[0]} />
                            <LabValue label="a*" value={reading.lab[1]} />
                            <LabValue label="b*" value={reading.lab[2]} />
                            <Stack sx={{ ml: "auto", alignItems: "flex-end" }}>
                                <Typography sx={{ fontSize: 10, color: MUTED, lineHeight: 1.2 }}>ΔE00 between repetitions</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, fontVariantNumeric: "tabular-nums" }}>
                                    {reading.deltaE.toFixed(2)}
                                    {limit != null && (
                                        <Box component="span" sx={{ color: MUTED, fontWeight: 400 }}>{` / ${limit}`}</Box>
                                    )}
                                </Typography>
                            </Stack>
                        </Stack>
                        {showDrift && reading.drift_deltaE != null && (
                            <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.5 }}>
                                {`Drift from the exploration value: ΔE00 ${reading.drift_deltaE.toFixed(2)}`}
                            </Typography>
                        )}
                    </Box>
                );
            })}
            <Stack
                direction="row" alignItems="center" justifyContent="space-between"
                sx={{ pt: 1, borderTop: "1px solid rgba(255,255,255,0.12)" }}
            >
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: MUTED }}>
                    By eye
                </Typography>
                {!cell.repeatable ? (
                    <VerdictChip tone="muted">not checked</VerdictChip>
                ) : cell.manual_rejected ? (
                    <VerdictChip tone="reject">Rejected eye</VerdictChip>
                ) : (
                    <VerdictChip tone="pass">Pass eye</VerdictChip>
                )}
            </Stack>
            <Stack
                direction="row" alignItems="center" justifyContent="space-between"
                sx={{ pt: 1, borderTop: "1px solid rgba(255,255,255,0.12)" }}
            >
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: MUTED }}>
                    Overall
                </Typography>
                <VerdictChip tone={cell.kept ? "pass" : "reject"}>
                    {cell.kept ? "Retained" : cellStateLabel(cell)}
                </VerdictChip>
            </Stack>
        </Stack>
    );
}
